// File: src/pages/Agents.js
import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import Footer from '../components/Footer';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 60px 0;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
  margin: 0 -20px 60px -20px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    margin: 0 -20px 40px -20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 40px;
  opacity: 0.95;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 30px;
  }
`;

const AgentTypeButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const AgentTypeButton = styled(NavLink)`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.3);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.6);
    transform: translateY(-2px);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 40px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const AgentTypesSection = styled.section`
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const AgentTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 40px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const AgentTypeCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const AgentTypeIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
`;

const AgentTypeTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 15px;
  color: #333;
`;

const AgentTypeSubtitle = styled.p`
  font-size: 1.2rem;
  color: #2ecc71;
  font-weight: 600;
  margin-bottom: 20px;
`;

const AgentTypeDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
  margin-bottom: 30px;
`;

const FeatureItem = styled.li`
  padding: 8px 0;
  color: #333;
  position: relative;
  padding-left: 25px;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #2ecc71;
    font-weight: bold;
  }
`;

const CTAButton = styled(NavLink)`
  display: inline-block;
  background: #3498db;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2980b9;
    transform: translateY(-2px);
  }
`;

const ProfilesSection = styled.section`
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ProfileImage = styled.div`
  height: 200px;
  background: linear-gradient(45deg, #3498db, #2c3e50);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
`;

const ProfileContent = styled.div`
  padding: 25px;
`;

const ProfileName = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 8px;
  color: #333;
`;

const ProfileRole = styled.div`
  color: #3498db;
  font-weight: 600;
  margin-bottom: 15px;
`;

const ProfileQuote = styled.p`
  font-style: italic;
  color: #666;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const ProfileStats = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 15px;
  border-top: 1px solid #eee;
  font-size: 0.9rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-weight: 600;
  color: #3498db;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.8rem;
`;

const TestimonialsSection = styled.section`
  background: #f8f9fa;
  padding: 60px 0;
  margin: 0 -20px 0 -20px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const TestimonialCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 20px;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  font-weight: 600;
  color: #3498db;
`;

const TestimonialRole = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 5px;
`;

const ProcessSection = styled.section`
  margin-bottom: 80px;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const ProcessStep = styled.div`
  text-align: center;
  padding: 30px 20px;
`;

const ProcessNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #3498db;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 auto 20px auto;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 15px;
  color: #333;
`;

const ProcessDescription = styled.p`
  color: #666;
  line-height: 1.5;
`;

const Agents = ({ user }) => {
  return (
    <>
      <Container>
        <HeroSection>
          <HeroTitle>Built for Real Estate Professionals</HeroTitle>
          <HeroSubtitle>
            Discover how ShowingBooker helps listing agents and buyer's agents streamline their workflow and grow their business
          </HeroSubtitle>
          <AgentTypeButtons>
            <AgentTypeButton to="#listing-agents">For Listing Agents</AgentTypeButton>
            <AgentTypeButton to="#buyers-agents">For Buyer's Agents</AgentTypeButton>
          </AgentTypeButtons>
        </HeroSection>

        <AgentTypesSection>
          <AgentTypeGrid>
            <AgentTypeCard id="listing-agents">
              <AgentTypeIcon>🏠</AgentTypeIcon>
              <AgentTypeTitle>Listing Agents</AgentTypeTitle>
              <AgentTypeSubtitle>100% FREE Forever</AgentTypeSubtitle>
              <AgentTypeDescription>
                Complete property showing management with tenant compliance, automated notifications, and professional booking pages.
              </AgentTypeDescription>
              <FeatureList>
                <FeatureItem>Automated 24-hour tenant notices</FeatureItem>
                <FeatureItem>Professional property booking pages</FeatureItem>
                <FeatureItem>Calendar integration and scheduling</FeatureItem>
                <FeatureItem>Tenant compliance documentation</FeatureItem>
                <FeatureItem>Lead capture and management</FeatureItem>
                <FeatureItem>Unlimited property listings</FeatureItem>
                <FeatureItem>Custom availability schedules</FeatureItem>
              </FeatureList>
              <CTAButton to="/register">Get Started Free</CTAButton>
            </AgentTypeCard>

            <AgentTypeCard id="buyers-agents">
              <AgentTypeIcon>🔍</AgentTypeIcon>
              <AgentTypeTitle>Buyer's Agents</AgentTypeTitle>
              <AgentTypeSubtitle>$10 per booking OR $29/month unlimited</AgentTypeSubtitle>
              <AgentTypeDescription>
                Professional booking tools that impress clients and generate leads while maintaining compliance with tenant-occupied properties.
              </AgentTypeDescription>
              <FeatureList>
                <FeatureItem>Professional client impression</FeatureItem>
                <FeatureItem>Access to all listed properties</FeatureItem>
                <FeatureItem>Automated calendar invitations</FeatureItem>
                <FeatureItem>Client communication tools</FeatureItem>
                <FeatureItem>Lead generation opportunities</FeatureItem>
                <FeatureItem>Mobile-optimized booking</FeatureItem>
                <FeatureItem>Compliance with tenant rights</FeatureItem>
              </FeatureList>
              <CTAButton to="/pricing">View Pricing Options</CTAButton>
            </AgentTypeCard>
          </AgentTypeGrid>
        </AgentTypesSection>

        <ProfilesSection>
          <SectionTitle>Agent Success Stories</SectionTitle>
          <ProfileGrid>
            <ProfileCard>
              <ProfileImage>👩‍💼</ProfileImage>
              <ProfileContent>
                <ProfileName>Sarah Chen</ProfileName>
                <ProfileRole>Listing Agent, Coldwell Banker</ProfileRole>
                <ProfileQuote>
                  "ShowingBooker completely transformed how I handle tenant-occupied properties. The automated compliance saves me 5+ hours per week."
                </ProfileQuote>
                <ProfileStats>
                  <StatItem>
                    <StatNumber>47</StatNumber>
                    <StatLabel>Properties</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>280</StatNumber>
                    <StatLabel>Showings</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>100%</StatNumber>
                    <StatLabel>Compliance</StatLabel>
                  </StatItem>
                </ProfileStats>
              </ProfileContent>
            </ProfileCard>

            <ProfileCard>
              <ProfileImage>👨‍💼</ProfileImage>
              <ProfileContent>
                <ProfileName>Michael Rodriguez</ProfileName>
                <ProfileRole>Buyer's Agent, Compass</ProfileRole>
                <ProfileQuote>
                  "My clients are always impressed by the professional booking experience. It sets me apart from other agents immediately."
                </ProfileQuote>
                <ProfileStats>
                  <StatItem>
                    <StatNumber>156</StatNumber>
                    <StatLabel>Bookings</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>23</StatNumber>
                    <StatLabel>Closes</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>4.9</StatNumber>
                    <StatLabel>Rating</StatLabel>
                  </StatItem>
                </ProfileStats>
              </ProfileContent>
            </ProfileCard>

            <ProfileCard>
              <ProfileImage>👩‍💼</ProfileImage>
              <ProfileContent>
                <ProfileName>Jennifer Park</ProfileName>
                <ProfileRole>Buyer's Agent, Keller Williams</ProfileRole>
                <ProfileQuote>
                  "The $10 booking fee pays for itself with just one deal. The lead generation alone is worth the investment."
                </ProfileQuote>
                <ProfileStats>
                  <StatItem>
                    <StatNumber>89</StatNumber>
                    <StatLabel>Bookings</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>12</StatNumber>
                    <StatLabel>Leads</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>18%</StatNumber>
                    <StatLabel>Close Rate</StatLabel>
                  </StatItem>
                </ProfileStats>
              </ProfileContent>
            </ProfileCard>
          </ProfileGrid>
        </ProfilesSection>

        <ProcessSection>
          <SectionTitle>How It Works</SectionTitle>
          <ProcessGrid>
            <ProcessStep>
              <ProcessNumber>1</ProcessNumber>
              <ProcessTitle>Sign Up</ProcessTitle>
              <ProcessDescription>
                Create your agent profile and verify your license. Listing agents get immediate free access.
              </ProcessDescription>
            </ProcessStep>
            
            <ProcessStep>
              <ProcessNumber>2</ProcessNumber>
              <ProcessTitle>Add Properties</ProcessTitle>
              <ProcessDescription>
                Upload your listings with photos, details, and custom availability schedules for tenant-occupied units.
              </ProcessDescription>
            </ProcessStep>
            
            <ProcessStep>
              <ProcessNumber>3</ProcessNumber>
              <ProcessTitle>Share & Book</ProcessTitle>
              <ProcessDescription>
                Share professional booking links with clients. All compliance, notifications, and scheduling handled automatically.
              </ProcessDescription>
            </ProcessStep>
            
            <ProcessStep>
              <ProcessNumber>4</ProcessNumber>
              <ProcessTitle>Manage Showings</ProcessTitle>
              <ProcessDescription>
                View your calendar, manage prospects, export to your preferred calendar app, and track all activity.
              </ProcessDescription>
            </ProcessStep>
          </ProcessGrid>
        </ProcessSection>
      </Container>

      <TestimonialsSection>
        <Container>
          <SectionTitle>What Agents Are Saying</SectionTitle>
          <TestimonialGrid>
            <TestimonialCard>
              <TestimonialText>
                "I was skeptical about the tenant compliance features, but ShowingBooker's automated 24-hour notices have saved me from potential legal issues multiple times."
              </TestimonialText>
              <TestimonialAuthor>David Kim</TestimonialAuthor>
              <TestimonialRole>Listing Agent, RE/MAX</TestimonialRole>
            </TestimonialCard>
            
            <TestimonialCard>
              <TestimonialText>
                "The $29/month unlimited plan paid for itself in week one. I book 20+ showings monthly and my clients love the professional experience."
              </TestimonialText>
              <TestimonialAuthor>Lisa Wong</TestimonialAuthor>
              <TestimonialRole>Buyer's Agent, Century 21</TestimonialRole>
            </TestimonialCard>
            
            <TestimonialCard>
              <TestimonialText>
                "ShowingBooker helped me land my biggest listing because the owner was impressed by my tech-forward approach to property management."
              </TestimonialText>
              <TestimonialAuthor>Robert Martinez</TestimonialAuthor>
              <TestimonialRole>Listing Agent, Sotheby's</TestimonialRole>
            </TestimonialCard>
            
            <TestimonialCard>
              <TestimonialText>
                "The calendar integration is seamless. All my showings automatically sync to my Google Calendar and my clients get professional invitations."
              </TestimonialText>
              <TestimonialAuthor>Amanda Foster</TestimonialAuthor>
              <TestimonialRole>Buyer's Agent, Compass</TestimonialRole>
            </TestimonialCard>
          </TestimonialGrid>
        </Container>
      </TestimonialsSection>

      <Footer />
    </>
  );
};

export default Agents;